import React, { useState, useEffect } from "react";
import TableInputs from "../../components/tableinputs/TableInputs";
import { Form } from "react-bootstrap";
import { ContactData } from "../../controller/ContactData";
import RefreshIcon from "@mui/icons-material/Refresh";
import { AxiosInstance } from "../../axios";
import {
  linkedin,
  twitter,
  facebook,
  Instagram,
  blue_email,
  blue_location,
  blue_phone,
  blue_social,
} from "../../controller/Images";
import Style from "./LetsTalk.module.css";
import Buttons from "../../components/buttons/Buttons";
import ModalComponent from "../../components/modalcomponent/ModalComponent";
import ToastMessage from "../../components/messagetoast/ToastMessage";
import SeoPage from "../../components/seo/SeoPage";

const generateCaptcha = () => {
  const chars =
    "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let captcha = "";
  for (let i = 0; i < 6; i++) {
    captcha += chars[Math.floor(Math.random() * chars.length)];
  }
  return captcha;
};

function LetsTalk() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    companyName: "",
    companyUrl: "",
    captcha: "",
    countryCode: "", // Added countryCode to formData
  });
  const [applicantType, setApplicantType] = useState("company");
  const [captchaText, setCaptchaText] = useState(generateCaptcha());
  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);
  const [phoneCodes, setPhoneCodes] = useState([]); // State for phone codes
  const [submitted, setSubmitted] = useState(false); // Track if form was submitted

  // toast for backend messages / feedback
  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "danger",
  });

  // Fetch phone codes on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const phoneCodeRes = await AxiosInstance.get(
          "/api/joinourteam/listphonecode",
        );
        if (
          phoneCodeRes.data.status &&
          Array.isArray(phoneCodeRes.data.phonecode)
        ) {
          const fetchedPhoneCodes = phoneCodeRes.data.phonecode.map(
            (codeObj, index) => ({
              label: `+${codeObj.phonecode}`,
              value: `${codeObj.phonecode}`,
              key: `phonecode-${codeObj.phonecode}-${index}`,
            }),
          );
          setPhoneCodes(fetchedPhoneCodes);

          if (fetchedPhoneCodes.length > 0) {
            const defaultPhoneCode =
              fetchedPhoneCodes.find((pc) => pc.value === "91") ||
              fetchedPhoneCodes[0];
            setFormData((prev) => ({
              ...prev,
              countryCode: defaultPhoneCode.value,
            }));
          } else {
            setFormData((prev) => ({
              ...prev,
              countryCode: "",
            }));
          }
        } else {
          setPhoneCodes([]);
        }
      } catch (err) {
        console.error("Error fetching phone codes:", err);
        setErrors((prev) => ({
          ...prev,
          phoneCode: "Failed to load phone codes.",
        }));
        setPhoneCodes([]);
      }
    };

    fetchData();
  }, []);

  // Helper: stricter email regex
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  // Helper: URL regex (basic but robust)
  const urlRegex =
    /^(https?:\/\/)?([A-Za-z0-9-]+\.)+[A-Za-z]{2,}(\/[A-Za-z0-9._~:/?#[\]@!$&'()*+,;=%-]*)?$/;

  // Map of country codes to typical national number lengths (not exhaustive)
  const phoneLengthMap = {
    91: 10, // India
    1: 10, // USA/Canada
    44: 10, // UK (varies but 10 typical national without leading 0)
    61: 9, // Australia (usually 9)
    7: 10, // Russia
    81: 10, // Japan (varies)
    49: 10, // Germany (varies)
    // ... more mappings can be added as needed
  };

  const getPhoneValidation = (countryCodeValue, phoneValue) => {
    if (!phoneValue) return "Phone is required";
    const digitsOnly = phoneValue.replace(/\D/g, ""); // remove non-digit
    if (!digitsOnly) return "Phone must contain digits";

    // Normalize countryCode (strip + if any)
    const cc = ("" + (countryCodeValue || "")).replace(/^\+/, "");
    if (cc && cc in phoneLengthMap) {
      const requiredLen = phoneLengthMap[cc];
      return digitsOnly.length === requiredLen
        ? ""
        : `Phone must be ${requiredLen} digits for country code +${cc}`;
    }

    // fallback: accept 6-14 digits
    if (digitsOnly.length >= 6 && digitsOnly.length <= 14) {
      return "";
    }
    return "Phone number length invalid (should be 6 to 14 digits for selected country)";
  };

  const validateField = (name, value) => {
    // Note: value may be undefined for phone because we need countryCode too; handle in caller
    switch (name) {
      case "firstName": {
        const trimmedValue = value?.trim();

        if (!trimmedValue) {
          return "First Name is required";
        }

        if (trimmedValue.length < 3) {
          return "First Name must be at least 3 characters";
        }

        // Emoji check
        const emojiRegex = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;
        if (emojiRegex.test(trimmedValue)) {
          return "First Name should not contain emojis";
        }

        // Numbers check
        const numberRegex = /\d/;
        if (numberRegex.test(trimmedValue)) {
          return "First Name should not contain numbers";
        }

        // Special characters check
        const specialCharRegex = /[!@#$%^&*()_+}{":?><]/;
        if (specialCharRegex.test(trimmedValue)) {
          return "First Name should not contain special characters";
        }

        return "";
      }

      case "lastName": {
        const trimmedValue = value?.trim();

        if (!trimmedValue) {
          return "Last Name is required";
        }

        if (trimmedValue.length < 1) {
          return "Last Name must be at least 1 character";
        }

        // Emoji check
        const emojiRegex = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;
        if (emojiRegex.test(trimmedValue)) {
          return "Last Name should not contain emojis";
        }

        // Numbers check
        const numberRegex = /\d/;
        if (numberRegex.test(trimmedValue)) {
          return "Last Name should not contain numbers";
        }

        // Special characters check
        const specialCharRegex = /[!@#$%^&*()_+}{":?><]/;
        if (specialCharRegex.test(trimmedValue)) {
          return "Last Name should not contain special characters";
        }

        return "";
      }

      case "email":
        if (!value || !value.toString().trim()) return "Email is required";
        return emailRegex.test(value) ? "" : "Invalid email address";
      case "phone":
        // phone validation depends on countryCode
        return getPhoneValidation(formData.countryCode, value);
      case "message": {
        const trimmed = value?.trim() || "";

        if (!trimmed) return "Message is required.";
        if (trimmed.length < 30)
          return "Message must be at least 30 characters long.";

        return "";
      }

      case "companyName":
        if (applicantType === "company") {
          const trimmedValue = value?.trim();

          // Required check
          if (!trimmedValue) {
            return "Company Name is required";
          }

          // Minimum length check
          if (trimmedValue.length < 3) {
            return "Company Name must be at least 3 characters";
          }

          // Emoji check
          const emojiRegex =
            /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;
          if (emojiRegex.test(trimmedValue)) {
            return "Company Name should not contain emojis";
          }

          // Special characters check
          const specialCharRegex = /[!@#$%^&*()_+}{":?><]/;
          if (specialCharRegex.test(trimmedValue)) {
            return "Company Name should not contain special characters";
          }
        }
        return "";

      case "companyUrl":
        if (applicantType === "company") {
          if (!value || !value.toString().trim())
            return "Website URL is required";
          return urlRegex.test(value) ? "" : "Invalid website URL";
        }
        return "";
      case "captcha":
        return value && value.trim() ? "" : "Please enter CAPTCHA";
      case "countryCode": {
        // Reject 0 or +0 or empty
        if (!value) return "Country code is required";
        const normalized = ("" + value).replace(/^\+/, "");
        if (!/^\d+$/.test(normalized)) return "Country code must be numeric";
        if (normalized === "0") return "Invalid country code";
        // optional: reject obviously invalid ones like '000'
        if (/^0+$/.test(normalized)) return "Invalid country code";
        return "";
      }
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this specific field when user starts typing
    if (submitted && errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }

    // Special case: when countryCode changes, also clear phone error if exists
    if (name === "countryCode" && submitted && errors.phone) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        phone: "",
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true); // Mark that form was submitted

    const newErrors = {};

    // Validate every relevant field
    const fieldsToValidate = [
      ...(applicantType === "company" ? ["companyName", "companyUrl"] : []),
      "firstName",
      "lastName",
      "email",
      "phone",
      "message",
      "captcha",
      "countryCode",
    ];

    fieldsToValidate.forEach((key) => {
      const value = formData[key];
      let error = "";
      if (key === "phone")
        error = getPhoneValidation(formData.countryCode, value);
      else error = validateField(key, value);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // captcha match check
    if ((formData.captcha || "").trim() !== captchaText) {
      setErrors((prev) => ({
        ...prev,
        captcha: "Invalid CAPTCHA. Please try again.",
      }));
      setToast({
        show: true,
        message: "CAPTCHA mismatch. Please enter correct CAPTCHA.",
        variant: "danger",
      });
      return;
    }

    // Clear all errors if validation passes
    setErrors({});

    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone_number: formData.phone, // Send phone number separately
      phone_code: formData.countryCode, // Send phone_code separately
      message: formData.message,
      typeof_client: applicantType === "company" ? 0 : 1,
    };

    if (applicantType === "company") {
      payload.company_name = formData.companyName;
      payload.website_url = formData.companyUrl;
    }

    AxiosInstance.post("/api/letstalk/create", payload)
      .then((res) => {
        // Handle typical responses: res.data.status true/false, res.data.message, res.data.errors
        if (res?.data?.status) {
          setOpen(true);
          setToast({
            show: true,
            message: res.data.message || "Submitted successfully.",
            variant: "success",
          });
        } else {
          // Collect backend error messages and show via toast + put field errors if provided
          const backendMessage = res?.data?.message || "Submission failed";
          let backendErrors = {};
          if (res?.data?.errors && typeof res.data.errors === "object") {
            // Map backend errors to fields if possible and join messages
            Object.entries(res.data.errors).forEach(([k, v]) => {
              // v can be array or string
              const msg = Array.isArray(v) ? v.join(", ") : String(v);
              backendErrors[k] = msg;
            });
          }

          // Merge backend field errors into UI errors (mapping keys if backend uses different names)
          const mappedErrors = { ...backendErrors };
          // possible mapping: phone_code -> countryCode, phone_number -> phone, first_name -> firstName, etc.
          if (backendErrors.phone_number)
            mappedErrors.phone = backendErrors.phone_number;
          if (backendErrors.phone_code)
            mappedErrors.countryCode = backendErrors.phone_code;
          if (backendErrors.first_name)
            mappedErrors.firstName = backendErrors.first_name;
          if (backendErrors.last_name)
            mappedErrors.lastName = backendErrors.last_name;
          if (backendErrors.company_name)
            mappedErrors.companyName = backendErrors.company_name;
          if (backendErrors.website_url)
            mappedErrors.companyUrl = backendErrors.website_url;
          if (backendErrors.email) mappedErrors.email = backendErrors.email;

          setErrors(mappedErrors);

          // Build toast message showing either the server message or collected errors
          let toastMsg = backendMessage;
          if (Object.keys(backendErrors).length > 0) {
            const combined = Object.values(backendErrors).join(" | ");
            toastMsg = toastMsg ? `${toastMsg} - ${combined}` : combined;
          }

          setToast({
            show: true,
            message: toastMsg,
            variant: "danger",
          });
        }

        // On both success & failure set/reset form as appropriate (here only on success we might want reset)
        if (res?.data?.status) {
          setFormData({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            message: "",
            companyName: "",
            companyUrl: "",
            captcha: "",
            countryCode:
              phoneCodes.find((pc) => pc.value === "91")?.value ||
              phoneCodes[0]?.value ||
              "",
          });
          setErrors({});
          setCaptchaText(generateCaptcha());
          setSubmitted(false); // Reset submitted state on success
        }
      })
      .catch((err) => {
        console.error("Submission error:", err);
        // Try to show server message if present
        const serverMsg =
          err?.response?.data?.message ||
          (err?.response?.data?.errors
            ? Object.values(err.response.data.errors).join(" | ")
            : null) ||
          err.message ||
          "Failed to submit form. Please try again.";
        setToast({
          show: true,
          message: serverMsg,
          variant: "danger",
        });
      });
  };

  const handleRadioChange = (e) => {
    setApplicantType(e.target.value);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      message: "",
      companyName: "",
      companyUrl: "",
      captcha: "",
      countryCode:
        phoneCodes.find((pc) => pc.value === "91")?.value ||
        phoneCodes[0]?.value ||
        "",
    });
    setErrors({});
    setSubmitted(false); // Reset submitted state when switching applicant type
  };

  return (
    <>
      <SeoPage
        title="Let’s Talk | Get a Free Demo and Speak with Our Experts Now"
        description="Get your free demo now and connect with our experts to explore smart solutions needed to your business goals. - Bharat clouds"
      />
      <div className={`container-fluid pt-md-5 px-md-5 ${Style.main_div}`}>
        {/* Toast for backend messages */}
        <ToastMessage
          show={toast.show}
          onClose={() => setToast({ ...toast, show: false })}
          message={toast.message}
          variant={toast.variant}
        />

        <div className="row">
          {/* Right Column: Form - This will now appear first on mobile due to order-1 */}
          <div className="col-12 col-md-7 order-1 order-md-2 ">
            <div className={`card p-4 shadow ${Style.Custom_Card}`}>
              <h5 className={` mb-3 main_subheading`}>
                Fill out the form below and we'll get back to you soon.
              </h5>

              <div className={`row ${Style.form_label}`}>
                <label className="form-label">Contact As</label>

                <div className="d-flex gap-3 px-4" style={{ margin: "5px" }}>
                  <Form.Check
                    className={`custom-radio ${Style.label_text} p-0`}
                    type="radio"
                    label="Company"
                    name="contactAs"
                    id="company"
                    value="company"
                    checked={applicantType === "company"}
                    onChange={handleRadioChange}
                  />
                  <Form.Check
                    className={`custom-radio ${Style.label_text}`}
                    type="radio"
                    label="Individual"
                    value="candidate"
                    name="contactAs"
                    id="individual"
                    checked={applicantType === "candidate"}
                    onChange={handleRadioChange}
                  />
                </div>

                <form onSubmit={handleSubmit}>
                  {applicantType === "candidate" ? (
                    <>
                      <div className="row">
                        <div className="col-md-6">
                          <TableInputs
                            label="First Name"
                            name="firstName"
                            placeholder="Enter first name"
                            value={formData.firstName}
                            onChange={handleChange}
                          />
                          {submitted && errors.firstName && (
                            <span className="custom_error">
                              {errors.firstName}
                            </span>
                          )}
                        </div>
                        <div className="col-md-6">
                          <TableInputs
                            label="Last Name"
                            name="lastName"
                            placeholder="Enter last name"
                            value={formData.lastName}
                            onChange={handleChange}
                          />
                          {submitted && errors.lastName && (
                            <span className="custom_error">
                              {errors.lastName}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <TableInputs
                            label="Email Address"
                            type="email"
                            name="email"
                            placeholder="Enter email address"
                            value={formData.email}
                            onChange={handleChange}
                          />
                          {submitted && errors.email && (
                            <span className="custom_error">{errors.email}</span>
                          )}
                        </div>
                        <div className="col-md-6">
                          <Form.Label className="form-label">
                            Phone Number
                          </Form.Label>
                          <div className="d-flex gap-2">
                            <div
                              style={{ maxWidth: "120px", flex: "0 0 auto" }}
                            >
                              <TableInputs
                                name="countryCode"
                                id="countryCode"
                                type="select"
                                value={formData.countryCode}
                                onChange={handleChange}
                                options={phoneCodes}
                                placeholder="+91"
                              />
                              {submitted && errors.countryCode && (
                                <span className="custom_error">
                                  {errors.countryCode}
                                </span>
                              )}
                            </div>
                            <div className="flex-grow-1">
                              <TableInputs
                                name="phone"
                                id="phone"
                                type="Number"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="Enter your phone number"
                              />
                              {submitted && errors.phone && (
                                <span className="custom_error">
                                  {errors.phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-12 ">
                          <TableInputs
                            label="Message"
                            name="message"
                            type="textarea"
                            placeholder="Briefly tell us about your needs..."
                            value={formData.message}
                            onChange={handleChange}
                            row="3"
                            showWordCount={true}
                          />
                          {submitted && errors.message && (
                            <span className="custom_error">
                              {errors.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="row">
                        <div className="col-md-6 ">
                          <TableInputs
                            label="Company Name"
                            name="companyName"
                            placeholder="Enter company name"
                            value={formData.companyName}
                            onChange={handleChange}
                          />
                          {submitted && errors.companyName && (
                            <span className="custom_error">
                              {errors.companyName}
                            </span>
                          )}
                        </div>
                        <div className="col-md-6 ">
                          <TableInputs
                            label="Website URL"
                            name="companyUrl"
                            placeholder="Enter company website URL"
                            value={formData.companyUrl}
                            onChange={handleChange}
                          />
                          {submitted && errors.companyUrl && (
                            <span className="custom_error">
                              {errors.companyUrl}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <TableInputs
                            label="First Name"
                            name="firstName"
                            placeholder="Enter first name"
                            value={formData.firstName}
                            onChange={handleChange}
                          />
                          {submitted && errors.firstName && (
                            <span className="custom_error">
                              {errors.firstName}
                            </span>
                          )}
                        </div>
                        <div className="col-md-6 ">
                          <TableInputs
                            label="Last Name"
                            name="lastName"
                            placeholder="Enter last name"
                            value={formData.lastName}
                            onChange={handleChange}
                          />
                          {submitted && errors.lastName && (
                            <span className="custom_error">
                              {errors.lastName}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <TableInputs
                            label="Email Address"
                            type="email"
                            name="email"
                            placeholder="Enter email address"
                            value={formData.email}
                            onChange={handleChange}
                          />
                          {submitted && errors.email && (
                            <span className="custom_error">{errors.email}</span>
                          )}
                        </div>
                        <div className="col-md-6 mb-2">
                          <Form.Label className="form-label">
                            Phone Number
                          </Form.Label>
                          <div className="d-flex gap-2">
                            <div
                              style={{ maxWidth: "120px", flex: "0 0 auto" }}
                            >
                              <TableInputs
                                name="countryCode"
                                id="countryCode"
                                type="select"
                                value={formData.countryCode}
                                onChange={handleChange}
                                options={phoneCodes}
                                placeholder="+91"
                              />
                              {submitted && errors.countryCode && (
                                <span className="custom_error">
                                  {errors.countryCode}
                                </span>
                              )}
                            </div>
                            <div className="flex-grow-1">
                              <TableInputs
                                name="phone"
                                id="phone"
                                type="Number"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="Enter your phone number"
                              />
                              {submitted && errors.phone && (
                                <span className="custom_error">
                                  {errors.phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-12 ">
                          <TableInputs
                            label="Message"
                            name="message"
                            type="textarea"
                            placeholder="Briefly tell us about your needs..."
                            value={formData.message}
                            onChange={handleChange}
                            showWordCount={true}
                          />
                          {submitted && errors.message && (
                            <span className="custom_error">
                              {errors.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="row">
                    <div className="col-md-12 mb-2">
                      <label className="form-label">Verification Code</label>
                      <div className="custom-captcha-wrapper d-flex align-items-center gap-2">
                        <input
                          type="text"
                          name="captcha"
                          className="form-control w-50"
                          placeholder="Enter the CAPTCHA code"
                          value={formData.captcha}
                          onChange={handleChange}
                        />
                        <span className="captcha-text">{captchaText}</span>
                        <span
                          className="refresh-icon"
                          onClick={() => setCaptchaText(generateCaptcha())}
                          role="button"
                          tabIndex={0}
                        >
                          <RefreshIcon
                            style={{ color: "#FA840E", cursor: "pointer" }}
                            onClick={() => setCaptchaText(generateCaptcha())}
                          />
                        </span>
                      </div>
                      {submitted && errors.captcha && (
                        <span className="custom_error">{errors.captcha}</span>
                      )}
                    </div>
                  </div>

                  <div className="row align-items-center mt-2 ">
                    <div className="col-12 col-lg-9 order-1 order-lg-1">
                      <h5 className="main_subheading">
                        By submitting this form, you accept the privacy policy
                        of our site.
                      </h5>
                    </div>

                    <div className="col-12 col-lg-3 order-2 order-lg-2 ">
                      <Buttons type="submit" text="Let's Talk" />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Left Column: Contact Info - This will now appear second on mobile due to order-2 */}
          <div className="col-12 col-md-5 mb-4 mb-md-0 order-2 order-md-1">
            <h5 className={`main_heading ${Style.contact_heading}`}>
              Let's Talk Innovations
            </h5>
            <div className="mt-4 d-flex align-items-center">
              <img src={blue_email} alt="email" height="30" className="me-3" />
              <div className="d-flex flex-column justify-content-center">
                <div className={Style.contact_title}>Chat with us</div>
                <p className={`mb-0 main_paragraph"`}>{ContactData[0]?.text}</p>
              </div>
            </div>
            <ModalComponent
              show={open}
              onHide={() => setOpen(false)}
              title="Submitted Successfully"
              bodyText="Thank you for your interest in Bharat Clouds. Your message is valuable to us, and we will ensure it reaches the team best suited to help you. We will get back to you shortly."
              buttontext="OK"
            />

            <div className="mt-4 d-flex align-items-center">
              <img
                src={blue_location}
                alt="location"
                height="40"
                className="me-3"
              />
              <div className="d-flex flex-column justify-content-center">
                <div className={Style.contact_title}>Visit us</div>
                <p className={`mb-0 main_paragraph`}>{ContactData[2]?.text}</p>
              </div>
            </div>
            <div className="mt-4 d-flex align-items-center">
              <img src={blue_phone} alt="phone" height="30" className="me-3" />
              <div className="d-flex flex-column justify-content-center">
                <div className={Style.contact_title}>Call us</div>
                <p className={`mb-0 main_paragraph`}>{ContactData[1]?.text}</p>
              </div>
            </div>
            <div className="mt-4 d-flex align-items-center">
              <img src={blue_social} alt="phone" height="30" className="me-3" />
              <div className="d-flex flex-column justify-content-center">
                <div className={Style.contact_title}>Follow us</div>
                <ul className="list-unstyled d-flex gap-3 m-0">
                  <li>
                    <a
                      href="https://www.linkedin.com/company/bharatclouds/about/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src={linkedin} alt="linkedin Icon" height="20" />
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://x.com/BharatClouds"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src={twitter} alt="twitter Icon" height="20" />
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.facebook.com/bharatcloudspvtltd/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src={facebook} alt="facebook Icon" height="20" />
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.instagram.com/bharatclouds/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src={Instagram} alt="Instagram Icon" height="20" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default LetsTalk;
